import type { AiMode } from '@hugo/core'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { ApiError } from '@/lib/api'
import { formatUsd } from '@/lib/format'
import { queryKeys, useAiStatus, useRunTool, useSettings } from '@/lib/items'

const MODE_LABELS: Record<AiMode, string> = {
  off: 'Off',
  fake: 'Fake (test server)',
  replay: 'Replay',
  record: 'Live (recording)',
  live: 'Live',
}

/**
 * Settings → hugo's AI: how it runs on this server, whether hugo files clear asks by itself
 * (`aiAutoFile`), and today's AI cost against the daily budget. With the AI off the card says so
 * and has nothing to switch.
 */
export function AiCard() {
  const status = useAiStatus()
  const settings = useSettings()
  const queryClient = useQueryClient()
  const { run } = useRunTool()
  /** The value being saved, shown right away; the server's once it answered. */
  const [saving, setSaving] = useState<boolean | null>(null)
  const ai = status.data
  const autoFile = saving ?? settings.data?.aiAutoFile ?? ai?.autoFile ?? false

  const setAutoFile = async (next: boolean) => {
    setSaving(next)
    const result = await run(
      'settings.update',
      { aiAutoFile: next },
      {
        done: next ? 'hugo files clear asks for you' : 'hugo only suggests now',
        failed: 'Could not change auto-filing',
      },
    )
    if (result) queryClient.setQueryData(queryKeys.settings, result)
    void queryClient.invalidateQueries({ queryKey: queryKeys.ai })
    setSaving(null)
  }

  return (
    <Card data-testid="ai-settings">
      <CardHeader>
        <CardTitle>hugo's AI</CardTitle>
        <CardDescription data-testid="ai-description">
          {ai && !ai.enabled
            ? "hugo's AI is off on this server: no suggestions, no filing by hugo and no drafts."
            : 'hugo files an ask itself only when it is sure, never as FYI or ignore, and you can undo it. Nothing is sent without your approval.'}
        </CardDescription>
        {ai ? (
          <CardAction>
            <Badge variant="outline" data-testid="ai-mode">
              {MODE_LABELS[ai.mode]}
            </Badge>
          </CardAction>
        ) : null}
      </CardHeader>
      {ai ? (
        ai.enabled ? (
          <CardContent>
            <div className="flex items-center justify-between gap-4 pb-2">
              <Label htmlFor="ai-auto-file" className="font-normal">
                File clear asks for me
              </Label>
              <Switch
                id="ai-auto-file"
                checked={autoFile}
                disabled={saving !== null || !settings.data}
                onCheckedChange={(checked) => void setAutoFile(checked)}
              />
            </div>
            <Separator />
            <p className="pt-2 text-sm text-muted-foreground tabular-nums" data-testid="ai-today">
              Today: {formatUsd(ai.today.costUsd)} of {formatUsd(ai.today.budgetUsd)}
            </p>
          </CardContent>
        ) : null
      ) : (
        <CardContent>
          {status.isError ? (
            <p className="text-sm text-destructive">
              Could not load hugo's AI.{' '}
              {status.error instanceof ApiError ? status.error.message : ''}
            </p>
          ) : (
            <Skeleton className="h-16 w-full" />
          )}
        </CardContent>
      )}
    </Card>
  )
}
